import { Injectable } from "@angular/core";
import { Transmission } from "@nms-ng2/app/modules/topology/links/links.model";
import { AnyDrawCommand } from "@nms-ng2/app/shared/services/layout-manager/drawing.models";
import { TranslationHelperService } from "@nms-ng2/app/shared/services/util/translation-helper.service";
import { DwdmDiagramAbstractConverterLinks } from "../../diagram/converter/dwdm-diagram-abstract-converter-links";
import {
    EndpointElementIdentifier,
    IntermediateElementIdentifier,
    LinksConverterData
} from "../../diagram/converter/dwdm-diagram-converter.models";
import {
    CircuitDirection,
    CircuitElementState,
    LinkElement
} from "../../diagram/models/diagram.models";
import { LinkInfoCircuit } from "../steps/4-links/links-step.model";
import { LinkRemovalEventService } from "../steps/4-links/services/link-removal-event.service.component";

/**
 * Implementação da @see DwdmDiagramAbstractConverterLinks para a wizard de criação de circuitos DWDM.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmDiagramWizardConverterLinksService extends DwdmDiagramAbstractConverterLinks {

    constructor(private readonly translationService: TranslationHelperService,
            private readonly linkRemovalEventService: LinkRemovalEventService) {
        super();
    }

    public convert(linksData: LinksConverterData, aEndIdentifier: EndpointElementIdentifier,
        zEndIdentifier: EndpointElementIdentifier, intermediatesIdentifier: IntermediateElementIdentifier[]): LinkElement[] {

        return super.convertData(linksData, aEndIdentifier, zEndIdentifier, intermediatesIdentifier,
            (linkElement: LinkElement, link: LinkInfoCircuit) => {
                const isOnLinkStep = linksData.isOnLinkStep;

                linkElement.canRemove = (linkElement.state === CircuitElementState.CONFIGURED) && isOnLinkStep;
                linkElement.getCustomTooltip = this.buildTooltip(link);
                linkElement.onRemove = (command: AnyDrawCommand) => {
                    this.linkRemovalEventService.emit({ id: link.id, identifier: command.parameters.value });
                };
            });
    }

    protected shouldCreateDefaultLink(linksData: LinksConverterData): boolean {
        return !linksData.links;
    }

    /**
     * Os Links default são mostrados quando não há intermediários e ainda não temos links configurados.
     * São os links tracejados em cinza claro que ligam sempre o A-END ao Z-END do circuito.
     */
    protected buildDefaultLink(linksData: LinksConverterData, aEndIdentifier: EndpointElementIdentifier,
            zEndIdentifier: EndpointElementIdentifier): LinkElement[] {
        const circuitAEnd = aEndIdentifier.endpoint;
        const circuitZEnd = zEndIdentifier.endpoint;
        const isOnLinkStep = linksData.isOnLinkStep;
        const state = CircuitElementState.NOT_CONFIGURED;

        return [
            this.buildLink(
                { id: circuitAEnd.id, transmission: Transmission.TX },
                { id: circuitZEnd.id, transmission: Transmission.RX },
                CircuitDirection.TOWARDS_Z_END,
                state,
                isOnLinkStep
            ),
            this.buildLink(
                { id: circuitZEnd.id, transmission: Transmission.TX },
                { id: circuitAEnd.id, transmission: Transmission.RX },
                CircuitDirection.TOWARDS_A_END,
                state,
                isOnLinkStep
            ),
        ];
    }

    /**
     * A atenuação no tooltip não deve ser mostrada quando a informação do link (attenuation) for 0.
     * Nesse caso será mostrado apenas o nome do link.
     */
    private buildTooltip(link: LinkInfoCircuit): Function {
        const name = link.name;
        const attenuation = parseFloat(link.attenuation);
        const distance = parseFloat(link.distance);
        let tooltip = name;
        const totalAttenuation = distance * attenuation;

        if (totalAttenuation > 0) {
            const complement = this.translationService.translateWithReplacement("dwdm.circuit.diagram.tooltip.link.attenuation",
                String(totalAttenuation), link.distance, link.attenuation);
            tooltip += `<br>${complement}`;
        }

        return () => tooltip;
    }
}
